import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis, MoneyAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
    formatTooltipData,
} from "./utils/filterChartData";

export function BalanceOverTime({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const filteredData = filterNullValuesFromChartData(data || []);
    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Chart
            title="Balance Over Time"
            description="Shows balance vs. at-risk capital over time for a credit."
            isCondensed={isCondensed}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "bar",
                        xKey: "month",
                        yKey: "balanceAvgInPeriod",
                        yName: "Avg. Loan Balance (Remittance Report)",
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                return {
                                    heading: `${month}`,
                                    title: "Average Loan Balance",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt Balance BOP",
                                            value: formatCurrency(
                                                datum.debtBalanceBop,
                                            ),
                                        },
                                        {
                                            label: "Disbursement",
                                            value: formatCurrency(
                                                datum.disbursement,
                                            ),
                                        },
                                        {
                                            label: "Amortization",
                                            value: formatCurrency(
                                                datum.amortization,
                                            ),
                                        },
                                        {
                                            label: "Interest & Fees",
                                            value: formatCurrency(
                                                datum.interestAndFees,
                                            ),
                                        },
                                        {
                                            label: "Interest & Fees Accrued",
                                            value: formatCurrency(
                                                datum.interestAndFeesAccrued,
                                            ),
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                    {
                        type: "bar",
                        xKey: "month",
                        yKey: "capitalAtRiskAvgInPeriod",
                        yName: "Average Loan Capital at Risk (Remittance Report)",
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                return {
                                    heading: `${month}`,
                                    title: "Capital at Risk",
                                    data: formatTooltipData([
                                        {
                                            label: "Capital at Risk BOP",
                                            value: formatCurrency(
                                                datum.capitalAtRiskBop,
                                            ),
                                        },
                                        {
                                            label: "Disbursement",
                                            value: formatCurrency(
                                                datum.disbursement,
                                            ),
                                        },
                                        {
                                            label: "Amortization",
                                            value: formatCurrency(
                                                datum.amortization,
                                            ),
                                        },
                                        {
                                            label: "Interest & Fees Cash",
                                            value: formatCurrency(
                                                datum.interestAndFeesCash,
                                            ),
                                        },
                                        {
                                            label: "Capital at Risk EOP",
                                            value: formatCurrency(
                                                datum.capitalAtRiskEop,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        ...MoneyAxis,
                        keys: [
                            "balanceAvgInPeriod",
                            "capitalAtRiskAvgInPeriod",
                        ],
                    },
                ],
            }}
        />
    );
}
